import {readPreference, writePreferences} from '@/services/userProfile';

interface ExportFieldChildSetting {
  label: string;
  value: boolean;
}

interface ExportFieldSetting {
  label: string;
  value: boolean | 'indeterminate';
  children?: ExportFieldChildSetting[];
}

type ExportFieldSettings = Map<string, ExportFieldSetting[]>;

interface SavedExportFieldSelections {
  version: number;
  tabs: Record<string, string[]>;
}

/** Bump when changing the serialized shape. Older versions are ignored on load. */
const VERSION = 1;

/**
 * Walk `ExportFieldSettings` and return a plain `{[tab]: string[]}` map of selected leaf
 * full-paths.
 */
const serializeSelections = (exportFieldSettings: ExportFieldSettings): Record<string, string[]> => {
  const tabs: Record<string, string[]> = {};
  exportFieldSettings.forEach((settings, tab) => {
    const selected: string[] = [];
    settings.forEach((setting) => {
      if (setting.children) {
        setting.children.forEach((child) => {
          if (child.value === true) {
            selected.push(`${setting.label}.${child.label}`);
          }
        });
      } else if (setting.value === true) {
        selected.push(setting.label);
      }
    });
    tabs[tab] = selected;
  });
  return tabs;
};

/**
 * Apply a previously-saved selection over a freshly-built `ExportFieldSettings` map.
 */
const applySavedSelections = (
  exportFieldSettings: ExportFieldSettings,
  saved: SavedExportFieldSelections | null | undefined,
): void => {
  if (!saved || !saved.tabs) return;
  exportFieldSettings.forEach((settings, tab) => {
    const savedList = saved.tabs[tab];
    if (!savedList) return;
    const savedSet = new Set(savedList);
    settings.forEach((setting) => {
      if (setting.children) {
        setting.children.forEach((child) => {
          child.value = savedSet.has(`${setting.label}.${child.label}`);
        });
        // Recompute the parent state from its children.
        if (!setting.children.length) return;
        const allTrue = setting.children.every((c) => c.value === true);
        const allFalse = setting.children.every((c) => c.value === false);
        if (allTrue) {
          setting.value = true;
        } else if (allFalse) {
          setting.value = false;
        } else {
          setting.value = 'indeterminate';
        }
      } else {
        setting.value = savedSet.has(setting.label);
      }
    });
  });
};

/**
 * Build the save/load pair for one export feature's field-selection prefs.
 *
 * @param prefsField - Firestore field on `beyondMaps-users/{uid}` where the last export's
 *   selection is stored (e.g. `'sharedMapExportFields'`).
 * @param featureLabel - Human label used in warn messages (e.g. `'shared-map'`).
 */
const createExportFieldPrefs = (prefsField: string, featureLabel: string) => {
  /**
   * Load the user's last-saved export-field selection.
   */
  const loadExportFieldSelections = async (): Promise<SavedExportFieldSelections | null> => {
    try {
      const saved = await readPreference<SavedExportFieldSelections>(prefsField);
      if (!saved || saved.version !== VERSION) return null;
      return saved;
    } catch (error) {
      console.warn(`Failed to load ${featureLabel} export field selections:`, error);
      return null;
    }
  };

  const saveExportFieldSelections = async (exportFieldSettings: ExportFieldSettings): Promise<void> => {
    try {
      const tabs = serializeSelections(exportFieldSettings);
      // An export only builds settings for the sheets currently on screen, so merge over the
      // saved tabs — a partial export must not wipe the selections of tabs it didn't show.
      const saved = await loadExportFieldSelections();
      await writePreferences({
        [prefsField]: {version: VERSION, tabs: {...saved?.tabs, ...tabs}},
      });
    } catch (error) {
      console.warn(`Failed to save ${featureLabel} export field selections:`, error);
    }
  };

  return {saveExportFieldSelections, loadExportFieldSelections};
};

export type {ExportFieldChildSetting, ExportFieldSetting, ExportFieldSettings, SavedExportFieldSelections};
export {serializeSelections, applySavedSelections, createExportFieldPrefs};
